import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { api, labUrlFor, shortSku, shortWax, fmtDate } from './wickTestApi';
import './WickTesting.css';

/**
 * Wick test labels — one label per candidate candle. Scanning the QR opens
 * the lab form for that candle.
 *
 *   /production/wick-test-labels?ids=<request ids>   (empty = every open request)
 *
 * Sheet mode prints on Avery 5160 (2.625" x 1", 30 per Letter sheet).
 * Roll mode prints one 2" x 1" label per page for a thermal label printer.
 * Browser print dialog: Margins = None, Scale = 100%, headers/footers off.
 */
export default function WickTestLabels() {
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('sheet');
  const [startPos, setStartPos] = useState(1);
  const [onlyUnpoured, setOnlyUnpoured] = useState(false);

  const ids = useMemo(() => (params.get('ids') || '').split(',').map((s) => s.trim()).filter(Boolean), [params]);

  useEffect(() => {
    api.requests('queued,poured,testing')
      .then((all) => setRows(ids.length ? all.filter((r) => ids.includes(r.id)) : all))
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, [ids]);

  const labels = [];
  for (const r of rows) {
    for (const c of r.candidates || []) {
      if (onlyUnpoured && c.status !== 'planned') continue;
      labels.push({ r, c });
    }
  }

  // Sheet layout: pad to the start position, then chunk into 30-up pages.
  const pages = [];
  if (mode === 'sheet') {
    const padded = [...Array(Math.max(0, Math.min(30, startPos) - 1)).fill(null), ...labels];
    for (let i = 0; i < padded.length; i += 30) pages.push(padded.slice(i, i + 30));
  }

  return (
    <div className="wt-app">
      <div className="wt-labels-controls wt-no-print">
        <strong>{labels.length} label{labels.length === 1 ? '' : 's'}</strong>
        <div className="wt-segment">
          <button className={mode === 'sheet' ? 'active' : ''} onClick={() => setMode('sheet')}>Avery 5160 sheet</button>
          <button className={mode === 'roll' ? 'active' : ''} onClick={() => setMode('roll')}>2" x 1" roll</button>
        </div>
        {mode === 'sheet' && (
          <label className="wt-note">Start at label <input className="wt-input" type="number" min={1} max={30} value={startPos} onChange={(e) => setStartPos(Number(e.target.value) || 1)} style={{ width: 64 }} /></label>
        )}
        <label className="wt-note"><input type="checkbox" checked={onlyUnpoured} onChange={(e) => setOnlyUnpoured(e.target.checked)} /> only not-yet-poured</label>
        <span className="wt-spacer" />
        <span className="wt-note">Print: Margins = None, Scale = 100%, headers off.</span>
        <button className="wt-btn" onClick={() => window.print()}>Print</button>
      </div>
      {error && <div className="wt-error">{error}</div>}

      {mode === 'sheet' && pages.map((page, pi) => (
        <div className="wt-sheet" key={pi}>
          {page.map((l, i) => (l ? <Label key={l.c.id} r={l.r} c={l.c} outline /> : <div key={`blank-${i}`} className="wt-label" />))}
        </div>
      ))}
      {mode === 'roll' && (
        <div className="wt-roll">
          {labels.map((l) => <Label key={l.c.id} r={l.r} c={l.c} />)}
        </div>
      )}
      {!labels.length && !error && <div className="wt-note">No candidates to label.</div>}
    </div>
  );
}

export function Label({ r, c, outline }) {
  return (
    <div className={`wt-label ${outline ? 'outline' : ''}`}>
      <div className="qr"><QRCodeSVG value={labUrlFor(c.code)} size={78} level="M" includeMargin={false} /></div>
      <div className="txt">
        <div className="code">{c.code}</div>
        <div className="wick">{c.wick_label}</div>
        <div className="line">{shortSku(r.oil_sku)}{r.size_label ? ` · ${r.size_label}` : ''}</div>
        <div className="line">{shortSku(r.vessel_sku)}</div>
        <div className="foot">{shortWax(r.wax_sku)} · {fmtDate(r.poured_at || new Date().toISOString())}</div>
      </div>
    </div>
  );
}
